import type { PickByValueExact } from 'utility-types'

import { assert, BelatedThrottler, createEqualityChecker, Droppable, encodeToBase64, executeOnce, type ReadonlyNonEmptyArray } from '../../../utils'

import {
    type AppState,
    arePaginationOptionsEquals,
    areSortConfigurationEquals,
    ConnectwareError,
    ConnectwareErrorType,
    type Page,
    type PaginationOptions,
    type PaginationParameters,
    type SortableColumn,
} from '../../../domain'

import {
    type ConfigurationService,
    initialState,
    type PageSubscriptionsTypes,
    type SubscriptionFilterArgs,
    type SubscriptionPageEvent,
    type SubscriptionPageOptions,
} from '../..'
import { Usecase } from '..'

export type PageSubscriptionArgs = SubscriptionFilterArgs & Readonly<{ short: boolean }>
export type ResourcesPaginationSizes = ReturnType<ConfigurationService['getResourcesPaginationSizes']>
export type SubscriptionUpdateParameters<T> = Partial<Pick<PaginationParameters<T>, 'sort' | 'search'> & PaginationOptions>

/** The page sizes offered to the user: ascending, unique and never above the configured max */
export const toPageSizeOptions = ([short, medium, large, max]: ResourcesPaginationSizes): ReadonlyNonEmptyArray<number> => {
    const belowMax = [...new Set([short, medium, large])].filter((size) => size < max).sort((a, b) => a - b)
    return [...belowMax, max]
}

const areSubscriptionPageOptionsEquals: <T extends keyof PageSubscriptionsTypes>(a: SubscriptionPageOptions<T>, b: SubscriptionPageOptions<T>) => boolean =
    createEqualityChecker<SubscriptionPageOptions<keyof PageSubscriptionsTypes>>({
        search: null,
        sort: areSortConfigurationEquals,
        pagination: arePaginationOptionsEquals,
    }) as <T extends keyof PageSubscriptionsTypes>(a: SubscriptionPageOptions<T>, b: SubscriptionPageOptions<T>) => boolean

export abstract class ResourcePageSubscriptionUsecase<PageName extends keyof PageSubscriptionsTypes> extends Usecase {
    private readonly getThrottle = executeOnce(() => this.configurationService.getInteractionThrottle())

    /** The type of subscription that is being made */
    protected abstract readonly pageName: PageName

    /** Where the page should be stored in the state */
    protected abstract readonly pageAddress: keyof PickByValueExact<AppState, Page<PageSubscriptionsTypes[PageName]>>

    protected abstract readonly initialSortColumn: SortableColumn<PageSubscriptionsTypes[PageName]>

    readonly getPaginationSizes = executeOnce(() => this.configurationService.getResourcesPaginationSizes())

    readonly getResourcesSearchThreshold = executeOnce(() => this.configurationService.getResourcesSearchThreshold())

    private generatePersistenceName (short: boolean, { server = '', service = '', connection = '' }: SubscriptionFilterArgs): string {
        /** Generate a unique id for the tables that are initialized as short or as default alongside their filter parameters */
        return [this.pageName, this.pageAddress, String(short), encodeToBase64(server), encodeToBase64(service), encodeToBase64(connection)].join('_')
    }

    private setPageState (page: Page<PageSubscriptionsTypes[PageName]>): void {
        this.setState({ [this.pageAddress]: page })
    }

    private selectPageFromState (s: AppState): Page<PageSubscriptionsTypes[PageName]> {
        return s[this.pageAddress] as Page<PageSubscriptionsTypes[PageName]>
    }

    private selectLoadedPageFromState (s: AppState): Exclude<Page<PageSubscriptionsTypes[PageName]>, null> {
        const state = this.selectPageFromState(s)
        assert(state !== null, new ConnectwareError(ConnectwareErrorType.STATE, 'Resource page is not initialized', { pageAddress: this.pageAddress }))
        return state
    }

    private updatePageState (update: Partial<Page<PageSubscriptionsTypes[PageName]>>): void {
        this.setPageState({ ...this.selectLoadedPageFromState(this.getState()), ...update })
    }

    /** A search shorter than the threshold isn't run, so it counts as no search */
    private toEffectiveSearch (search: string | null): string | null {
        return search !== null && search.length >= this.getResourcesSearchThreshold() ? search : null
    }

    /** The options the subscription actually runs with, which differ from the page state only by a too-short search */
    private selectSubscriptionPageOptions (s: AppState): SubscriptionPageOptions<PageName> {
        const { search, sort, pagination } = this.selectLoadedPageFromState(s)
        return { search: this.toEffectiveSearch(search), sort, pagination }
    }

    private updateStateWithPageEvents (event: SubscriptionPageEvent<PageName>): void {
        let { pagination, data } = this.selectLoadedPageFromState(this.getState())

        if (ConnectwareError.is(event)) {
            data = event
        } else {
            data = { current: event.current, totalCount: event.totalCount }

            if (pagination.page !== event.page) {
                /** Re-map the current page to stay in sync with the BE */
                pagination = { ...pagination, page: event.page }
            }

            if (pagination.pageSize !== event.pageSize) {
                /** Re-map the current page size to stay in sync with the BE */
                pagination = { ...pagination, pageSize: event.pageSize }
            }
        }

        this.updatePageState({ pagination, data })
    }

    private initialize (short: boolean, filter: SubscriptionFilterArgs): void {
        const resourcePage = this.selectPageFromState(this.getState())

        assert(
            resourcePage === null,
            new ConnectwareError(ConnectwareErrorType.STATE, 'Resource page was already initialized', { pageAddress: this.pageAddress })
        )

        let { page, pageSize, sort, search } = this.tablePersistenceService.retrieveTable<PageSubscriptionsTypes[PageName]>(
            this.generatePersistenceName(short, filter)
        )

        /** Either use the customized page, or, if there is nothing customized, use the short or medium sizes */
        if (pageSize === undefined) {
            const [shortPage, mediumPage, , maxPage] = this.getPaginationSizes()
            pageSize = Math.min(short ? shortPage : mediumPage, maxPage)
        }

        this.setPageState({
            search: search === undefined ? null : search,
            sort: sort ? { asc: sort.asc, column: sort.column } : { asc: true, column: this.initialSortColumn },
            pagination: { pageSize, page: page === undefined ? 0 : page },
            data: null,
        })
    }

    subscribe ({ short, ...filter }: PageSubscriptionArgs): VoidFunction {
        const droppable = new Droppable()

        this.initialize(short, filter)

        const updateThrottler = new BelatedThrottler(this.getThrottle())

        /** Start subscriptions finally */
        this.subscriptionsService
            .subscribeToPage(this.pageName, { ...this.selectSubscriptionPageOptions(this.getState()), ...filter })
            .then((subscription) => {
                /** Finally set internal listener */
                droppable.onDrop(subscription.onData((event) => droppable.ifNotDropped(() => this.updateStateWithPageEvents(event))))

                /** Listen to the state for any changes to the pagination setup */
                droppable.onDrop(
                    this.subscribeToState(
                        (prev, curr) => areSubscriptionPageOptionsEquals(this.selectSubscriptionPageOptions(prev), this.selectSubscriptionPageOptions(curr)),
                        () =>
                            /** Schedule an update */
                            droppable.onDrop(
                                updateThrottler.run(() =>
                                    droppable.ifNotDropped(() => {
                                        const options = this.selectSubscriptionPageOptions(this.getState())

                                        this.tablePersistenceService.persistTable<PageSubscriptionsTypes[PageName]>(
                                            this.generatePersistenceName(short, filter),
                                            {
                                                page: options.pagination.page,
                                                pageSize: options.pagination.pageSize,
                                                sort: options.sort,
                                                search: options.search,
                                            }
                                        )

                                        subscription.update(options)
                                    })
                                )
                            )
                    )
                )

                /** Kill internal subscription when dropped */
                droppable.onDrop(() => subscription.stop())
            })
            /** Handle any issues that occur while setting up the subscription */
            .catch((e: ConnectwareError) => droppable.ifNotDropped(() => this.updateStateWithPageEvents(e)))
            /** Schedule reset as well */
            .finally(() => droppable.onDrop(() => this.setPageState(this.selectPageFromState(initialState))))

        return () => droppable.drop()
    }

    updateParameters ({ page, pageSize, sort, search }: SubscriptionUpdateParameters<PageSubscriptionsTypes[PageName]>): void {
        const state = this.getState()
        const current = this.selectLoadedPageFromState(state)

        let updated = {
            pagination: { ...current.pagination, page: page ?? current.pagination.page, pageSize: pageSize ?? current.pagination.pageSize },
            search: search !== undefined ? search : current.search,
            sort: sort ?? current.sort,
        }

        if (
            updated.sort !== current.sort ||
            this.toEffectiveSearch(updated.search) !== this.toEffectiveSearch(current.search) ||
            updated.pagination.pageSize !== current.pagination.pageSize
        ) {
            /** If updating the sorting, search or page size, reset user to the first page */
            updated = { ...updated, pagination: { ...updated.pagination, page: 0 } }
        }

        const isReloading = !areSubscriptionPageOptionsEquals(this.selectSubscriptionPageOptions(state), {
            ...updated,
            search: this.toEffectiveSearch(updated.search),
        })

        /** Without a change the subscription would act on, the loaded data stays, and only the typed search is kept */
        this.updatePageState(isReloading ? { ...updated, data: null } : { search: updated.search })
    }
}
